import { useState, type ReactNode } from 'react';
import { LineChart } from '@/components/charts/LineChart';
import { Sparkline } from '@/components/charts/Sparkline';
import {
  Badge,
  CapacityBar,
  Checkbox,
  EntityChip,
  Kbd,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stat,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Timeline,
  type BadgeVariant,
} from '@/components/ui';
import { Code, Section, SubTitle } from './Section';

/* Примитивы данных и форм (docs/design-patterns-v3.md §1–2): каждый тип данных — одно представление. */

const tones: BadgeVariant[] = ['neutral', 'coral', 'urgent', 'warn', 'success', 'info', 'ai'];
const trend = [24, 26, 25, 29, 31, 30, 34, 62, 78, 55, 41, 34];

function demoSeries() {
  const start = Date.now() - 6 * 3600 * 1000;
  return [
    {
      id: 'demo',
      label: 'Запросов в секунду',
      points: Array.from({ length: 73 }, (_, i) => ({
        x: start + i * 5 * 60 * 1000,
        y: Math.round(120 + 40 * Math.sin(i / 7) + 25 * Math.sin(i / 3.1) + (i > 50 && i < 58 ? 90 : 0)),
      })),
    },
  ];
}

function Row({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-5">
      <div className="flex flex-wrap items-baseline gap-3">
        <SubTitle>{title}</SubTitle>
        <span className="text-xs text-fg-muted">{hint}</span>
      </div>
      {children}
    </div>
  );
}

export function DataPrimitivesSection() {
  const [on, setOn] = useState(true);
  const [series] = useState(demoSeries);

  return (
    <Section
      id="data"
      title="Данные и формы"
      lead="Один тип данных — одно представление (грамматика композиции, §4.3). Статус — Badge solid, метрика — Stat, ёмкость — CapacityBar, сущность — EntityChip, время — Timeline, коллекция — Table, изменение во времени — LineChart одной семьи."
    >
      <Row
        title="Badge solid · soft"
        hint="статусы — solid + контрастный текст (R9); soft — для нейтральных меток"
      >
        <div className="flex flex-wrap items-center gap-2">
          {tones.map((t) => (
            <Badge key={t} variant={t} solid>
              {t}
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tones.map((t) => (
            <Badge key={t} variant={t}>
              {t}
            </Badge>
          ))}
        </div>
      </Row>

      <Row
        title="Stat · Sparkline · CapacityBar"
        hint="подпись · значение · единица · дельта по смыслу · тренд; ёмкость нейтральна до порога"
      >
        <div className="grid gap-4 md:grid-cols-4">
          <Stat
            label="CPU"
            value="34"
            unit="%"
            delta={{ value: 6, label: 'за час', goodDirection: 'down' }}
            trend={trend}
            trendTone="brand"
          />
          <Stat
            label="Аптайм"
            value="99,97"
            unit="%"
            delta={{ value: 0.02, label: 'за месяц' }}
            trend={[99.9, 99.92, 99.95, 99.97]}
          />
          <Stat
            label="Ёмкость"
            value="48"
            unit="из 60"
            footer={<CapacityBar value={80} className="w-full" />}
          />
          <Stat
            label="Диск /vds"
            value="93"
            unit="%"
            footer={<CapacityBar value={93} className="w-full" />}
          />
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <Sparkline values={trend} tone="muted" />
          <Sparkline values={trend} tone="brand" />
          <Sparkline values={[5, 4, 6, 3, 8, 12, 15]} tone="danger" />
          <Sparkline values={[1, 3, 2, 5, 4, 7, 9]} tone="success" />
        </div>
      </Row>

      <Row
        title="LineChart"
        hint="плавная кривая 2 px, градиент в прозрачность, наведение — пунктир к осям и тултип; ← → с клавиатуры"
      >
        <LineChart series={series} height={200} unit="rps" threshold={{ value: 220, label: 'порог' }} />
      </Row>

      <Row
        title="EntityChip · Timeline · Kbd"
        hint="сущность всегда чипом с иконкой типа; хронология — точка по типу события"
      >
        <div className="flex flex-wrap items-center gap-2">
          <EntityChip kind="host" name="host-338" meta="PVE" />
          <EntityChip kind="vm" name="VM 100101" dotClassName="bg-sev-4" />
          <EntityChip kind="location" name="Площадка А" />
          <EntityChip kind="user" name="Михаил" />
          <EntityChip kind="channel" name="#alerts-infra" />
          <EntityChip kind="ticket" name="TASK-412" size="md" />
          <span className="ml-4 inline-flex items-center gap-1 text-xs text-fg-muted">
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd> поиск
          </span>
        </div>
        <Timeline
          className="max-w-xl"
          items={[
            { id: '1', kind: 'ack', time: '12:19', title: 'Подтверждено', actor: 'Анна' },
            {
              id: '2',
              kind: 'ai',
              time: '12:14',
              title: 'AI: гипотеза по iowait',
              description: 'уверенность 0.72',
            },
            { id: '3', kind: 'alert', time: '12:13', title: 'Триггер High: LA > 8' },
            { id: '4', kind: 'resolve', time: 'вчера', title: 'Восстановлено' },
          ]}
        />
      </Row>

      <Row
        title="Switch · Checkbox · Select · Tabs"
        hint="тумблер — действует сразу; чекбокс — часть формы; Select — когда вариантов больше пяти"
      >
        <div className="grid gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <Switch
              checked={on}
              onCheckedChange={setOn}
              label="Уведомлять в Slack"
              description="все триггеры High и выше"
            />
            <Switch size="sm" label="Компактный режим" />
            <Checkbox defaultChecked label="High и Disaster" />
            <Checkbox
              checked="indeterminate"
              label="Частичный выбор"
              description="в шапке таблицы при выборе части строк"
            />
            <Checkbox disabled label="Недоступно" />
          </div>
          <div className="flex flex-col gap-4">
            <Select defaultValue="1h">
              <SelectTrigger aria-label="Окно тишины">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15m">15 минут</SelectItem>
                <SelectItem value="1h">1 час</SelectItem>
                <SelectItem value="4h">4 часа</SelectItem>
                <SelectItem value="morning">До утра</SelectItem>
              </SelectContent>
            </Select>
            <Tabs defaultValue="a">
              <TabsList>
                <TabsTrigger value="a">Обзор</TabsTrigger>
                <TabsTrigger value="b" count={12}>
                  Проблемы
                </TabsTrigger>
                <TabsTrigger value="c">История</TabsTrigger>
              </TabsList>
            </Tabs>
            <Tabs variant="pill" defaultValue="24h">
              <TabsList>
                <TabsTrigger value="1h">1 ч</TabsTrigger>
                <TabsTrigger value="24h">24 ч</TabsTrigger>
                <TabsTrigger value="7d">7 д</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </Row>

      <Row
        title="Table"
        hint="числа справа табличными цифрами; действия строки — по наведению; выбранная — bg-selected"
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Узел</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead numeric>VPS</TableHead>
              <TableHead numeric>LA</TableHead>
              <TableHead>Локация</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[
              { h: 'host-338', s: 'urgent' as const, st: 'High', vps: 48, la: '8,40', loc: 'Площадка А' },
              { h: 'host-011', s: 'warn' as const, st: 'Warning', vps: 61, la: '2,10', loc: 'Площадка Б' },
              { h: 'host-018', s: 'success' as const, st: 'OK', vps: 39, la: '0,42', loc: 'Площадка Д' },
            ].map((r, i) => (
              <TableRow key={r.h} interactive selected={i === 1}>
                <TableCell>
                  <EntityChip kind="host" name={r.h} />
                </TableCell>
                <TableCell>
                  <Badge variant={r.s} solid>
                    {r.st}
                  </Badge>
                </TableCell>
                <TableCell numeric>{r.vps}</TableCell>
                <TableCell numeric>{r.la}</TableCell>
                <TableCell muted>{r.loc}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="text-xs text-fg-subtle">
          Данные из <Code>tokens.ts</Code> не нужны — таблица показывает канонические представления типов.
        </p>
      </Row>
    </Section>
  );
}
